import FingerprintJS from '@fingerprintjs/fingerprintjs'
import {
  AlfredService,
  LogService,
  MySquid,
  User,
  localForageHelper,
  onConfigChange,
  onFirebaseConfigChange,
  onUserChange,
} from '@hub/shared'
import { getToken } from 'firebase/messaging'
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SubscriptionLike } from 'rxjs'

import './modal-notification.component.scoped.scss'
import { SqButton } from '@squidit/react-css'

const USER_KEY = 'hub_user_session_info'

declare global {
  interface Window {
    fbmSw: ServiceWorkerRegistration
  }
}

interface Props extends PropsWithChildren<React.HTMLAttributes<HTMLDivElement>> {
  open?: boolean
  setOpen?: (open: boolean) => void
  squidId: string
}

export default ({ open = false, setOpen, squidId }: Props) => {
  const onConfigChangeSubscription = useRef<SubscriptionLike | any>(undefined)
  const onUserChangeSubscription = useRef<SubscriptionLike | any>(undefined)
  const logService = useMemo(() => new LogService(), [])
  const alfredService = useMemo(() => new AlfredService(), [])
  const [t] = useTranslation('modalNotification')
  const [firebase, setFirebase] = useState<any>(null)
  const [contextOpen, setContextOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [width, setWidth] = useState(window.innerWidth)
  const [height, setHeight] = useState(window.innerHeight)
  const [config, setConfig] = useState<MySquid | any>(null)
  const [user, setUser] = useState<User | any>(null)

  interface INavigator extends Navigator {
    userAgentData: {
      mobile: boolean
      platform: string
    }
  }

  interface WindowWithWebkit extends Window {
    webkit?: {
      [key: string]: any
      messageHandlers: {
        [key: string]: {
          postMessage: (data: any) => void
        }
      }
    }
    navigator: INavigator
  }

  const thisWindow = window as unknown as WindowWithWebkit

  const sendInfosToIos = useCallback(async () => {
    if (squidId && user?.profileadditionalinfo?.email) {
      const fp = await FingerprintJS.load()
      const { visitorId } = await fp.get()
      const token: { JWT: string } | null = await localForageHelper.getItem(USER_KEY)
      const accessToken = token?.JWT.replace('Bearer ', '')
      thisWindow?.webkit?.messageHandlers?.myInterface?.postMessage({
        squidId,
        email: user?.profileadditionalinfo?.email,
        accessToken,
        deviceId: visitorId,
        type: thisWindow.navigator.userAgentData?.mobile ? 'mobile' : 'desktop',
        system: thisWindow.navigator.userAgentData?.platform,
      })
    }
  }, [
    squidId,
    user?.profileadditionalinfo?.email,
    thisWindow.navigator.userAgentData?.mobile,
    thisWindow.navigator.userAgentData?.platform,
    thisWindow?.webkit?.messageHandlers?.myInterface,
  ])

  const authorizeNotification = useCallback(async () => {
    if (!firebase?.messaging) {
      return null
    }
    if (!loading) {
      setLoading(true)
      const fp = await FingerprintJS.load()
      const { visitorId } = await fp.get()
      const messagingToken = await getToken(firebase?.messaging, {
        vapidKey: firebase?.vapidKey,
        serviceWorkerRegistration: window.fbmSw,
      })
        .then((currentToken) => {
          alfredService
            .sendNotificationsInfo({
              squidId,
              email: user?.profileadditionalinfo?.email,
              deviceId: visitorId,
              type: thisWindow.navigator.userAgentData?.mobile ? 'mobile' : 'desktop',
              system: thisWindow.navigator.userAgentData?.platform,
              token: currentToken,
            })
            .then(() => {
              if (setOpen && typeof setOpen === 'function') {
                setOpen(false)
              }
              return currentToken
            })
            .catch((error) => {
              logService.error({ message: 'Error on send notification info', error })
              return ''
            })
        })
        .catch((error) => {
          logService.error({ message: 'Error on get token', error })
          return ''
        })

      if (messagingToken) {
        onFirebaseConfigChange.next({
          ...firebase,
          messagingToken,
        })
      }
      setLoading(false)
      onClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebase])

  const onClose = () => {
    if (setOpen && typeof setOpen === 'function') {
      setOpen(false)
    }
  }

  useEffect(() => {
    const onFirebaseConfigChangeObservable: SubscriptionLike = onFirebaseConfigChange.subscribe((f) => {
      if (f?.messaging) {
        setFirebase(f)
      }
    })
    return () => {
      onFirebaseConfigChangeObservable?.unsubscribe()
    }
  }, [])

  useEffect(() => {
    onUserChangeSubscription.current = onUserChange.subscribe((userChanged: User) => {
      setUser(userChanged)
    })
    return () => {
      onUserChangeSubscription.current?.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (open !== contextOpen) {
      setContextOpen(open)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    localForageHelper.getItem('impersonateUser').then((impersonateUser) => {
      if (firebase?.messaging && 'Notification' in window && !impersonateUser) {
        if (Notification.permission === 'default') {
          if (setOpen && typeof setOpen === 'function') {
            setOpen(true)
          }
        } else if (Notification.permission === 'granted') {
          authorizeNotification()
        } else if (Notification.permission === 'denied') {
          onClose()
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebase?.messaging])

  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth)
      setHeight(window.innerHeight)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    onConfigChangeSubscription.current = onConfigChange.subscribe((configChanged: MySquid | any) => {
      if (configChanged?._id) {
        setConfig(configChanged)
      }
    })
    return () => {
      onConfigChangeSubscription.current?.unsubscribe()
    }
  }, [onConfigChangeSubscription])

  useEffect(() => {
    sendInfosToIos()
  }, [sendInfosToIos])

  if (config || !contextOpen) {
    return null
  }

  return open ? (
    <div className="modal-notification" style={{ width, height }}>
      <div className="content-modal">
        <div className="button-top-left">
          <div className="arrow display-flex justify-content-center align-items-center">
            <i className="fa-solid fa-arrow-up-left white fa-xl" />
          </div>
        </div>
        <div className="content">
          <h3 className="white">{t('title')}</h3>
          <p className="white">{t('text')}</p>
          <SqButton
            className="mt-3"
            size="sm"
            color="var(--primary_color)"
            borderColor="transparent"
            loading={loading}
            id="modal-contract-accepted-confirm"
            onClick={() => authorizeNotification()}
          >
            {t('confirm')}
          </SqButton>
        </div>
      </div>
    </div>
  ) : null
}
